import {
	usePreviewEmailTemplate,
	useResetEmailTemplate,
	useUpdateEmailTemplate,
} from '@dealstack/frontend/shared/components/EmailTemplates/hooks/useEmailTemplates';
import type { EmailTemplateScope } from '@dealstack/frontend/shared/components/EmailTemplates/EmailTemplates.types';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date';
import type { EditorTab } from './EmailTemplateEditorForm.types';
import { Badge, Button, Group, Skeleton, Stack, Text, Title } from '@mantine/core';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EmailEditorRef } from '@react-email/editor';
import { redo, redoDepth, undo, undoDepth } from '@tiptap/pm/history';
import type { PreviewDevice } from './EmailPreview.types';
import type {
	EmailTemplateDetailRes,
	EmailTemplatePreviewRes,
	UpdateEmailTemplateDto,
} from '@dealstack/shared/lib/generated';

import { useEmailTemplateAccess } from '../hooks/useEmailTemplateAccess';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { isEditorDocument, type EditorDocument } from '../EmailTemplates.types';
import { withoutMenuState } from '../extensions/conditionalNodes';
import { importLiquidConditionals } from '../utils/importLiquidConditionals';
import { liquidInlineContent } from '../extensions/liquidVariableNodes';
import { EditorToolbar } from './EditorToolbar';
import { EmailPreview } from './EmailPreview';
import { EmailSubjectBlock, type SubjectHandle } from './EmailSubjectBlock';
import { EmailTemplateInspectorPanel } from './EmailTemplateInspectorPanel';
import { LeaveWithoutSavingDrawer } from './LeaveWithoutSavingDrawer';
import { PreviewVariablesForm } from './PreviewVariablesForm';
import { ResetTemplateDrawer } from './ResetTemplateDrawer';
import { TemplateStatus } from './TemplateStatus';

const EmailTemplateWorkspace = lazy(() =>
	import('./EmailTemplateWorkspace').then((module) => ({
		default: module.EmailTemplateWorkspace,
	}))
);

interface Props {
	scope: EmailTemplateScope;
	detail: EmailTemplateDetailRes;
	onBack: () => void;
}

export const EmailTemplateEditorForm = ({ scope, detail, onBack }: Props) => {
	const tMipManage = useTranslationMipManage();
	const [subject, setSubject] = useState(detail.current.subject);
	const [bodyHtml, setBodyHtml] = useState(detail.current.contentHtml);
	const [bodyDocument, setBodyDocument] = useState<EditorDocument | null>(null);
	const [backgroundColor, setBackgroundColor] = useState(detail.backgroundColor);
	const [enabled, setEnabled] = useState(detail.enabled);
	const { canEdit } = useEmailTemplateAccess(scope);
	const [editorRef, setEditorRef] = useState<EmailEditorRef | null>(null);
	const [tab, setTab] = useState<EditorTab>('edit');
	const [device, setDevice] = useState<PreviewDevice>('desktop');
	const [previewValues, setPreviewValues] = useState<Record<string, string>>(detail.sampleData);
	const [insertTarget, setInsertTarget] = useState<'subject' | 'body'>('body');
	const [resetOpened, setResetOpened] = useState(false);
	const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
	const [historyVersion, setHistoryVersion] = useState(0);

	const subjectRef = useRef<SubjectHandle>(null);
	const lastSelectionRef = useRef<number | null>(null);

	/**
	 * A template only carries an editor document if one was seeded for it; the generator that
	 * produced them was removed, so anything added since opens with none. The editor accepts
	 * HTML as content, so fall back to the rendered body rather than mounting an empty
	 * document — which would blank the template on the next save.
	 */
	const initialContent = useMemo<EditorDocument | string>(
		() =>
			isEditorDocument(detail.current.contentJson)
				? importLiquidConditionals(detail.current.contentJson)
				: detail.current.contentHtml || {},
		[detail.current.contentJson, detail.current.contentHtml]
	);

	const { mutate: mutateUpdate, isPending: isSaving } = useUpdateEmailTemplate({ scope });
	const { mutate: mutateReset, isPending: isResetting } = useResetEmailTemplate({ scope });
	const previewMutation = usePreviewEmailTemplate({ scope });
	const { mutate: runPreview } = previewMutation;
	const [preview, setPreview] = useState<EmailTemplatePreviewRes | undefined>();

	const subjectDirty = subject !== detail.current.subject;
	const bodyDirty = bodyHtml !== detail.current.contentHtml;
	const backgroundColorDirty = backgroundColor !== detail.backgroundColor;
	const enabledDirty = enabled !== detail.enabled;
	const isDirty = subjectDirty || bodyDirty || backgroundColorDirty || enabledDirty;

	const editor = editorRef?.editor ?? null;

	useEffect(() => {
		const timeout = setTimeout(() => {
			runPreview(
				{
					type: detail.templateType,
					body: { subject, body: bodyHtml, backgroundColor, values: previewValues },
				},
				{
					onSuccess: (res) => {
						if (res.payload) {
							setPreview(res.payload);
						}
					},
				}
			);
		}, 500);
		return () => clearTimeout(timeout);
	}, [runPreview, detail.templateType, subject, bodyHtml, backgroundColor, previewValues]);

	useEffect(() => {
		if (!editor) {
			return;
		}
		// 'update', not 'transaction': a setState per transaction re-enters the loop.
		const handleUpdate = () => setHistoryVersion((previous) => previous + 1);
		const handleSelection = () => {
			if (editor.isFocused) {
				lastSelectionRef.current = editor.state.selection.to;
			}
		};
		const handleFocus = () => setInsertTarget('body');
		editor.on('update', handleUpdate);
		editor.on('selectionUpdate', handleSelection);
		editor.on('focus', handleFocus);
		return () => {
			editor.off('update', handleUpdate);
			editor.off('selectionUpdate', handleSelection);
			editor.off('focus', handleFocus);
		};
	}, [editor]);

	const handleSave = useCallback(() => {
		if (!isDirty || isSaving) {
			return;
		}
		const body: UpdateEmailTemplateDto = {};
		if (subjectDirty) {
			body.subject = subject;
		}
		if (bodyDirty && bodyDocument) {
			body.body = bodyHtml;
			body.bodyJson = withoutMenuState(bodyDocument);
		}
		if (enabledDirty) {
			body.enabled = enabled;
		}
		if (backgroundColorDirty) {
			body.backgroundColor = backgroundColor;
		}
		mutateUpdate({ type: detail.templateType, body });
	}, [
		isDirty,
		isSaving,
		subjectDirty,
		bodyDirty,
		backgroundColorDirty,
		enabledDirty,
		subject,
		bodyHtml,
		bodyDocument,
		backgroundColor,
		enabled,
		mutateUpdate,
		detail.templateType,
	]);

	const handleBlockedNavigation = useCallback(
		(proceed: () => void) => setPendingNavigation(() => proceed),
		[]
	);
	useUnsavedChangesGuard({ enabled: isDirty, onBlocked: handleBlockedNavigation });

	const handleSaveRef = useRef(handleSave);
	handleSaveRef.current = handleSave;
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
				event.preventDefault();
				handleSaveRef.current();
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, []);

	const insertVariable = (token: string) => {
		const name = token.replace(/[{}\s]/g, '');
		if (insertTarget === 'subject') {
			subjectRef.current?.insertVariable(name);
			return;
		}
		if (!editor) {
			return;
		}
		const position = lastSelectionRef.current ?? editor.state.doc.content.size;
		editor.chain().focus().insertContentAt(position, liquidInlineContent(token)).run();
	};

	const history = useMemo(
		() => ({
			canUndo: editor ? undoDepth(editor.state) > 0 : false,
			canRedo: editor ? redoDepth(editor.state) > 0 : false,
		}),
		[editor, historyVersion]
	);
	const version = detail.isCustomized
		? [
				detail.updatedAt
					? tMipManage('email_templates.list.version.updated', {
							defaultValue: 'Updated {{date}}',
							date: formatStringDateToUI(
								String(detail.updatedAt),
								DATE_FORMATS.UI_SHORT
							),
						})
					: undefined,
				detail.updatedBy
					? tMipManage('email_templates.list.version.updated_by', {
							defaultValue: 'by {{name}}',
							name: detail.updatedBy,
						})
					: undefined,
			]
				.filter(Boolean)
				.join(' · ')
		: undefined;

	return (
		<Stack gap="md" h="100%" style={{ minHeight: 0 }}>
			<ResetTemplateDrawer
				opened={resetOpened}
				loading={isResetting}
				onClose={() => setResetOpened(false)}
				onConfirm={() =>
					mutateReset(
						{ type: detail.templateType },
						{
							onSuccess: () => {
								setResetOpened(false);
								onBack();
							},
						}
					)
				}
			/>
			<LeaveWithoutSavingDrawer
				opened={pendingNavigation !== null}
				onClose={() => setPendingNavigation(null)}
				onDiscard={() => {
					const proceed = pendingNavigation;
					setPendingNavigation(null);
					proceed?.();
				}}
			/>

			<Group justify="space-between" align="center" wrap="nowrap">
				<Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
					<Title order={3} fw={600} c="dark.8" style={{ whiteSpace: 'nowrap' }}>
						{detail.name}
					</Title>
					{detail.isStale && (
						<Badge color="orange" variant="light" size="sm">
							{tMipManage('email_templates.status.update_available')}
						</Badge>
					)}
				</Group>

				<Group gap="sm" wrap="nowrap">
					{version && (
						<Text fz="0.75rem" c="gray.9" style={{ whiteSpace: 'nowrap' }}>
							{version}
						</Text>
					)}
					<TemplateStatus
						label={
							enabled
								? tMipManage('email_templates.status.active', {
										defaultValue: 'Active',
									})
								: tMipManage('email_templates.status.draft', {
										defaultValue: 'Draft',
									})
						}
						active={enabled}
						disabled={!canEdit}
						onToggle={setEnabled}
					/>

					<Button
						variant="default"
						h={40}
						onClick={() => setResetOpened(true)}
						loading={isResetting}
						disabled={!detail.isCustomized || !canEdit}
					>
						{tMipManage('email_templates.editor.reset_button')}
					</Button>
					<Button
						h={40}
						onClick={handleSave}
						loading={isSaving}
						disabled={!isDirty || !canEdit}
					>
						{tMipManage('email_templates.editor.save_button')}
					</Button>
				</Group>
			</Group>

			<Suspense fallback={<Skeleton height={520} radius="md" />}>
				<EmailTemplateWorkspace
					initialContent={initialContent}
					variables={detail.variables}
					backgroundColor={backgroundColor}
					editable={canEdit}
					previewing={tab === 'preview'}
					toolbarSlot={
						<EditorToolbar
							tab={tab}
							device={device}
							canUndo={history.canUndo}
							canRedo={history.canRedo}
							onTabChange={setTab}
							onDeviceChange={setDevice}
							onUndo={() => editor && undo(editor.state, editor.view.dispatch)}
							onRedo={() => editor && redo(editor.state, editor.view.dispatch)}
						/>
					}
					subjectSlot={
						<EmailSubjectBlock
							ref={subjectRef}
							value={detail.current.subject}
							variables={detail.variables}
							editable={canEdit}
							onChange={setSubject}
							onFocus={() => setInsertTarget('subject')}
						/>
					}
					previewSlot={
						<EmailPreview
							device={device}
							html={preview?.html ?? ''}
							subject={preview?.subject ?? ''}
							recipient={
								previewValues.investor_full_name ||
								previewValues.first_name ||
								undefined
							}
							loading={previewMutation.isPending && !preview}
							error={previewMutation.isError && !preview}
							height={680}
						/>
					}
					panelSlot={
						tab === 'edit' ? (
							<EmailTemplateInspectorPanel
								variables={detail.variables}
								onInsert={insertVariable}
								backgroundColor={backgroundColor}
								onBackgroundColorChange={setBackgroundColor}
							/>
						) : (
							<PreviewVariablesForm
								variables={detail.variables}
								values={previewValues}
								onChange={setPreviewValues}
								onReset={() => setPreviewValues(detail.sampleData)}
							/>
						)
					}
					onChange={(html, document) => {
						setBodyHtml(html);
						setBodyDocument(document);
					}}
					onReady={setEditorRef}
				/>
			</Suspense>
		</Stack>
	);
};
